/* eslint-disable @typescript-eslint/no-explicit-any */
import { Permit } from './branch/permit';
import { SchemaNode } from './node';
import { SchemaBranch } from './branch/schema';
import type { GeneratedSchema, Schema, SchemaOptions, SchemaScope, SchemaTree } from './types';
import { makeScope } from './utils';

export { SchemaBranch } from './branch/schema';
export { SchemaNode, type PermitNode } from './node';
export type { Permit, PermitBranch } from './branch/permit';
export * from './types';

/**
 * create access control schema from tree model. Return generated access control.
 *
 * ```js
 *
 * 	const schema = createSchema({
 * 		tweet: {
 * 			create: "create a blog post",
 * 			edit: "edit a blog post",
 * 			delete: "delete a blog post"
 * 		}
 * 	}, { prefix: 'com.tweeter', spacer: ':' })
 *
 * 	console.log(schema.tweet.create.$scope)
 * 	// output: 'com.tweeter:tweet:create'
 *
 *
 * ```
 */
export function createSchema<T extends SchemaTree<string>>(
	model: T,
	options: Partial<SchemaOptions> = {}
) {
	const spacer = options.spacer ?? '.';
	const prefix = options.prefix ?? '';

	const $makeScope = (...path: string[]) => makeScope(spacer, path);

	const travel = <V extends SchemaTree<string>>(branch: V, parent: string) => {
		type Passthrough = Record<string, unknown> | ((...args: unknown[]) => unknown);
		type Node = SchemaBranch<GeneratedSchema<V>> | SchemaNode | Passthrough;
		const entries: [path: string, node: Node][] = [];
		for (const [key, value] of Object.entries(branch)) {
			const scope = $makeScope(parent, key);
			if (typeof key !== 'string') break;
			if (key.startsWith('$') || key.startsWith('_'))
				throw new Error(
					`[Schema '${scope}${spacer}${key}']: character '$' or '_' is reserved as utility key and not allowed to be schema path.`
				);
			else if (typeof value === 'string') entries.push([key, new SchemaNode(scope, value)]);
			else if (typeof value === 'object' && !(value instanceof Array)) {
				const node = travel(value, scope);
				const $branch = new SchemaBranch(node, scope, spacer);
				entries.push([key, Object.assign($branch, node)]);
			} else {
				throw new Error(
					`[Schema '${scope}${spacer}${key}']: tree leaf node must be a string value but received ${typeof value}`
				);
			}
		}

		return Object.fromEntries(entries) as Schema<GeneratedSchema<V>>;
	};

	const root = travel(model, prefix);
	const $root = new SchemaBranch(root, prefix, spacer);
	return Object.assign($root, root) as Schema<GeneratedSchema<T>>;
}

/**
 * create permit object from exported schema.
 * usefull for ssr application when only need to create a permit on requested page.
 *
 * ```ts
 *
 * // on server side, send json data of schema access
 * const blogPageSchema = schema.blog.export().schema
 * const json = JSON.stringify(blogPageSchema)
 * type BlogPageSchema = typeof blogPageSchema
 *
 * // on client, parse json data
 * const schema = JSON.parse(json)
 * const permit = createPermit<BlogPageSchema>(schema, [])
 *
 *
 * ```
 */
export function createPermit<T extends SchemaTree<SchemaScope>>(schema: T, scopes: string[]) {
	return new Permit(schema, scopes);
}